import React from "react";
import { FileInput, FormDataConsumer } from "react-admin";
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import * as S from "./CustomFileInput.styles";

/**
 * CustomFileInput - Componente reutilizável para upload de arquivos com feedback visual
 * 
 * @param {string} source - Campo do formulário (padrão: "file")
 * @param {string} accept - Tipos de arquivo aceitos (ex: ".xml,application/xml")
 * @param {array} validate - Funções de validação do React Admin
 * @param {boolean} disabled - Se o input está desabilitado
 * @param {string} uploadText - Texto principal do placeholder de upload
 * @param {string} successText - Texto ao exibir arquivo selecionado (padrão: nome do arquivo)
 * @param {string} formatText - Texto de formato aceito
 * @param {string} uploadIconColor - Cor do ícone de upload (padrão: "#1976D2")
 * @param {string} successIconColor - Cor do ícone de sucesso (padrão: "#2e7e33")
 * @param {boolean} showFileName - Se deve mostrar o nome do arquivo após seleção (padrão: true)
 * @param {string} className - Classe CSS adicional
 * @param {object} sx - Estilos MUI customizados
 * @param {function} customPlaceholder - Função customizada para renderizar placeholder (formData) => ReactNode
 */
const CustomFileInput = ({
  source = "file",
  accept,
  validate,
  disabled = false,
  uploadText = "Arraste o arquivo aqui ou clique para selecionar",
  successText,
  isValid: isValidOverride,
  formatText = "Formato aceito",
  uploadIconColor = "#1976D2",
  successIconColor = "#2e7e33",
  showFileName = true,
  className = "",
  sx = {},
  customPlaceholder,
  ...props
}) => {
  const renderDefaultPlaceholder = (formData, isValidFile) => {
    const file = formData?.[source];
    const hasFile = file?.rawFile;

    if (hasFile && isValidFile) {
      return (
        <>
          <S.SuccessIconWrapper successIconColor={successIconColor}>
            <CheckCircleIcon />
          </S.SuccessIconWrapper>
          {showFileName && (
            <S.FileName>{successText || file.rawFile.name}</S.FileName>
          )}
          {formatText && (
            <S.FormatText>{formatText}</S.FormatText>
          )}
        </>
      );
    }

    return (
      <>
        <S.UploadIconWrapper uploadIconColor={uploadIconColor}>
          <CloudUploadIcon />
        </S.UploadIconWrapper>
        <S.UploadText>{uploadText}</S.UploadText>
        {formatText && <S.FormatText>{formatText}</S.FormatText>}
      </>
    );
  };

  return (
    <FormDataConsumer>
      {({ formData }) => {
        const hasFile = formData?.[source]?.rawFile;
        const isValidFile = typeof isValidOverride === "boolean" ? isValidOverride : !!hasFile;

        return (
          <S.StyledFileInput isValid={isValidFile}>
            <FileInput
              source={source}
              label={false}
              accept={accept}
              validate={validate}
              disabled={disabled}
              className={className}
              sx={sx}
              placeholder={
                customPlaceholder 
                  ? customPlaceholder(formData)
                  : renderDefaultPlaceholder(formData, isValidFile)
              }
              {...props}
            />
          </S.StyledFileInput>
        );
      }}
    </FormDataConsumer>
  );
};

export default CustomFileInput;
